import type { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function DetailPage({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn('w-full space-y-6', className)}>{children}</div>
}

export function DetailHeader({
  breadcrumbs,
  icon,
  title,
  description,
  actions,
  metrics,
  tabs,
  className,
}: {
  breadcrumbs?: ReactNode
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  metrics?: ReactNode
  tabs?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('overflow-hidden border-b border-border/30 bg-[#edf4fc]', className)}>
      <div className="space-y-4 px-5 py-4 sm:px-6 sm:py-5">
        {breadcrumbs ? (
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">{breadcrumbs}</div>
        ) : null}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex items-start gap-3">
              {icon ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background">
                  {icon}
                </div>
              ) : null}
              <div className="min-w-0 space-y-1.5">
                <h1 className="break-words text-[1.4rem] font-bold tracking-[-0.03em] text-foreground sm:text-[1.8rem]">
                  {title}
                </h1>
                {description ? (
                  <p className="max-w-3xl break-words text-sm leading-6 text-muted-foreground">{description}</p>
                ) : null}
              </div>
            </div>
          </div>
          {(actions || metrics) ? (
            <div className="flex min-w-0 flex-col gap-3 xl:items-end">
              {metrics ? (
                <div className="flex flex-wrap gap-3 border-border/30 xl:justify-end xl:border-r xl:pr-4">
                  {metrics}
                </div>
              ) : null}
              {actions ? <div className="flex flex-wrap items-center gap-2 xl:justify-end">{actions}</div> : null}
            </div>
          ) : null}
        </div>
      </div>
      {tabs ? <div className="px-5 sm:px-6">{tabs}</div> : null}
    </section>
  )
}

export function DetailMetric({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: ReactNode
  value: ReactNode
  detail?: ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info'
}) {
  return (
    <div
      className={cn(
        'min-w-[7rem] border-l border-border/30 pl-3 first:border-l-0 first:pl-0',
        tone === 'success' && 'text-success',
        tone === 'warning' && 'text-warning',
        tone === 'danger' && 'text-destructive',
        tone === 'info' && 'text-info',
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold text-foreground">{value}</p>
      {detail ? <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  )
}

export function DetailTabBar({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn('flex flex-wrap gap-6 border-b border-border/30', className)}>{children}</div>
}

export function DetailTab({
  icon,
  label,
  meta,
  active = false,
}: {
  icon?: ReactNode
  label: ReactNode
  meta?: ReactNode
  active?: boolean
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm transition-colors',
        active ? 'border-primary font-semibold text-foreground' : 'border-transparent text-muted-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
      {meta ? <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{meta}</span> : null}
    </div>
  )
}

export function DetailSection({
  title,
  description,
  action,
  children,
  className,
  contentClassName,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
}) {
  return (
    <Card className={cn('overflow-hidden rounded-xl border border-border/30 bg-background shadow-none', className)}>
      <CardHeader className="border-b border-border/20 bg-[#eef4fb]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 space-y-1">
            <CardTitle className="break-words text-base font-semibold">{title}</CardTitle>
            {description ? <CardDescription className="max-w-3xl break-words">{description}</CardDescription> : null}
          </div>
          {action ? <div className="flex min-w-0 flex-wrap items-center gap-2">{action}</div> : null}
        </div>
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  )
}

export function DetailSplit({
  sidebar,
  main,
  reverseOnDesktop = false,
  className,
}: {
  sidebar: ReactNode
  main: ReactNode
  reverseOnDesktop?: boolean
  className?: string
}) {
  return (
    <section
      className={cn(
        'grid gap-6',
        reverseOnDesktop ? 'xl:grid-cols-[minmax(0,1fr)_19rem]' : 'xl:grid-cols-[19rem_minmax(0,1fr)]',
        className,
      )}
    >
      {!reverseOnDesktop ? (
        <>
          <div className="min-w-0">{sidebar}</div>
          <div className="min-w-0">{main}</div>
        </>
      ) : (
        <>
          <div className="min-w-0">{main}</div>
          <div className="min-w-0">{sidebar}</div>
        </>
      )}
    </section>
  )
}
